import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { RealPhoto } from "@/components/site/RealPhoto";
import { getServiceBySlug, SERVICES, serviceFaqs, type ServiceDef } from "@/lib/services-catalog";
import { breadcrumb, faqPage, listingDetail } from "@/lib/seo-jsonld";
import { ArrowRight, Check, MapPin, ShieldCheck, Star, Wallet, Clock } from "lucide-react";
import heroImg from "@/assets/cat-moving.jpg";

export const Route = createFileRoute("/services/$slug")({
  loader: ({ params }) => {
    const service = getServiceBySlug(params.slug);
    if (!service) throw notFound();
    return { service };
  },
  head: ({ loaderData, params }) => {
    const service = loaderData?.service;
    const name = service?.name ?? params.slug;
    const title = `${name} in Greater London from £${service?.rateLow ?? 25}/hr — Peopels`;
    const description = service
      ? `${service.summary} Vetted local professionals across Greater London. Your card is securely authorized at booking — funds only released when the job is done.`
      : `${name} across Greater London with Peopels.`;
    const url = `/services/${params.slug}`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: service
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify(
                listingDetail({
                  id: service.slug,
                  name: `${service.name} in Greater London`,
                  description: service.summary,
                  category: service.name,
                  price: { value: service.rateLow, currency: "GBP" },
                  areaServed: "Greater London",
                  url,
                }),
              ),
            },
            {
              type: "application/ld+json",
              children: JSON.stringify(faqPage(serviceFaqs(service.slug, "Greater London"))),
            },
            {
              type: "application/ld+json",
              children: JSON.stringify(
                breadcrumb([
                  { name: "Home", path: "/" },
                  { name: "Services", path: "/categories" },
                  { name: service.name, path: url },
                ]),
              ),
            },
          ]
        : [],
    };
  },
  notFoundComponent: () => (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-16 text-center">
        <h1 className="font-display text-2xl font-bold">Service not found</h1>
        <Link to="/categories" className="mt-4 inline-block text-primary underline">Browse all services</Link>
      </main>
      <Footer />
    </div>
  ),
  errorComponent: () => (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-16 text-center">
        <h1 className="font-display text-2xl font-bold">Something went wrong</h1>
        <Link to="/categories" className="mt-4 inline-block text-primary underline">Browse all services</Link>
      </main>
      <Footer />
    </div>
  ),
  component: ServicePage,
});

function ServicePage() {
  const { service } = Route.useLoaderData() as { service: ServiceDef };
  const related = SERVICES.filter((s) => s.slug !== service.slug && s.parent === service.parent).slice(0, 3);
  const others = SERVICES.filter((s) => s.slug !== service.slug && s.parent !== service.parent).slice(0, 3);
  const faqs = serviceFaqs(service.slug, "Greater London");

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main id="main-content" className="mx-auto w-full max-w-5xl flex-1 px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
        <Breadcrumbs
          items={[
            { label: "Services", to: "/categories" },
            { label: service.name },
          ]}
        />

        <header className="mt-6 grid gap-8 lg:grid-cols-[1.2fr_1fr] lg:items-center">
          <div>
            <p className="inline-flex items-center gap-1.5 rounded-full bg-accent px-3 py-1 text-xs font-medium text-primary">
              <MapPin className="h-3.5 w-3.5" /> Greater London
            </p>
            <h1 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-5xl">
              {service.name} in Greater London
            </h1>
            <p className="mt-4 max-w-2xl text-base text-muted-foreground sm:text-lg">
              {service.summary}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/post-job"
                search={{ category: service.parent }}
                className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground shadow-card transition hover:shadow-elegant"
              >
                Post your {service.shortName.toLowerCase()} job <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/become-helper"
                className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold text-foreground transition hover:border-primary/30"
              >
                Offer this service
              </Link>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              From £{service.rateLow}/hr. A typical visit takes {Math.round(service.avgDurationMinutes / 15) * 15} mins, and your card is authorized at booking and only released when the job is done.
            </p>
          </div>
          <div className="relative aspect-[4/3] overflow-hidden rounded-3xl border border-border bg-muted">
            <RealPhoto category={service.slug} fallback={heroImg} alt={`${service.name} in a London home`} className="h-full w-full object-cover" />
          </div>
        </header>

        <section className="mt-12 grid gap-4 sm:grid-cols-3">
          {[
            { icon: ShieldCheck, title: "Verified professionals", body: "ID, contact and KYC checks on every Helper accepted onto Peopels." },
            { icon: Wallet, title: "Secure escrow payments", body: "Card authorized at booking — funds released to your Helper only after you confirm the job is done." },
            { icon: Star, title: "Real London reviews", body: "Read reviews from neighbours in your borough before you book." },
          ].map((b) => (
            <div key={b.title} className="rounded-2xl border border-border bg-card p-5">
              <b.icon className="h-5 w-5 text-primary" />
              <p className="mt-3 font-semibold">{b.title}</p>
              <p className="mt-1 text-sm text-muted-foreground">{b.body}</p>
            </div>
          ))}
        </section>

        <section className="mt-12 grid gap-8 lg:grid-cols-2">
          <div>
            <h2 className="font-display text-2xl font-bold">What's included</h2>
            <ul className="mt-4 space-y-2">
              {service.includes.map((it) => (
                <li key={it} className="flex items-start gap-2 text-sm">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="font-display text-2xl font-bold">Good for</h2>
            <ul className="mt-4 space-y-2">
              {service.goodFor.map((it) => (
                <li key={it} className="flex items-start gap-2 text-sm">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
            <h3 className="mt-6 font-semibold">Not covered</h3>
            <ul className="mt-2 space-y-1.5 text-sm text-muted-foreground">
              {service.notIncluded.map((it) => (
                <li key={it}>• {it}</li>
              ))}
            </ul>
          </div>
        </section>

        <section className="mt-12 rounded-3xl border border-border bg-card p-6 sm:p-8">
          <h2 className="font-display text-2xl font-bold">Why choose Peopels for {service.name.toLowerCase()}?</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <p className="text-sm text-muted-foreground">
              Peopels.com is built around Greater London — every Helper is local, vetted, and reviewed by neighbours in your borough.
              You'll see a clear price estimate before you commit, and your card is only authorized when you book.
            </p>
            <p className="text-sm text-muted-foreground">
              Funds sit safely in Stripe escrow until you confirm the work is done. No upfront charges, no hidden fees, and a real
              dispute team if anything doesn't go to plan.
            </p>
          </div>
          <div className="mt-6 flex flex-wrap gap-3 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-primary" /> Match in minutes</span>
            <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-primary" /> Greater London coverage</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-primary" /> ID-verified Helpers</span>
          </div>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-bold">Frequently asked questions</h2>
          <div className="mt-4 space-y-3">
            {faqs.map((f) => (
              <details key={f.q} className="rounded-2xl border border-border bg-card p-4">
                <summary className="cursor-pointer font-semibold">{f.q}</summary>
                <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mt-12 rounded-3xl bg-gradient-to-br from-primary/10 to-accent/40 p-8 text-center">
          <h2 className="font-display text-2xl font-bold sm:text-3xl">Need {service.name.toLowerCase()} today?</h2>
          <p className="mt-2 text-sm text-muted-foreground sm:text-base">
            Post your job in minutes. Your card is securely authorized — funds released only once the work is complete.
          </p>
          <Link
            to="/post-job"
            search={{ category: service.parent }}
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-card transition hover:shadow-elegant"
          >
            Post your job <ArrowRight className="h-4 w-4" />
          </Link>
        </section>

        {(related.length > 0 || others.length > 0) && (
          <section className="mt-12">
            <h2 className="font-display text-2xl font-bold">Related services</h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {[...related, ...others].slice(0, 6).map((s) => (
                <Link
                  key={s.slug}
                  to="/services/$slug"
                  params={{ slug: s.slug }}
                  className="rounded-2xl border border-border bg-card p-4 transition hover:-translate-y-0.5 hover:border-primary/30"
                >
                  <p className="font-semibold">{s.name}</p>
                  <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{s.summary}</p>
                  <p className="mt-2 text-xs text-primary">From £{s.rateLow}/hr</p>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
